import { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Download, Copy, Printer, Link2, QrCode } from 'lucide-react';
import { toast } from 'sonner';

const JobApplicationQR = () => {
  const qrRef = useRef(null);
  const applicationUrl = `${window.location.origin}/apply`;
  const statusUrl = `${window.location.origin}/apply/status`;

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  const downloadQR = (type) => {
    const svg = document.getElementById(`qr-${type}`);
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 1024;
      canvas.height = 1024;
      
      // White background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR code centered
      const padding = 100;
      ctx.drawImage(img, padding, padding, canvas.width - (padding * 2), canvas.height - (padding * 2));
      
      // Add text at bottom
      ctx.fillStyle = '#1e293b';
      ctx.font = 'bold 36px Arial';
      ctx.textAlign = 'center';
      const label = type === 'apply' ? 'Scan to Apply - Wilyrash Farm' : 'Check Application Status';
      ctx.fillText(label, canvas.width / 2, canvas.height - 30);
      
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `wilyrash-farm-${type}-qr.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const printQR = () => {
    const printWindow = window.open('', '_blank');
    const svg = document.getElementById('qr-apply');
    const svgData = new XMLSerializer().serializeToString(svg);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Job Application QR Code - Wilyrash Farm</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              padding: 40px;
              box-sizing: border-box;
            }
            .qr-container {
              text-align: center;
              border: 3px solid #059669;
              border-radius: 20px;
              padding: 40px;
              max-width: 400px;
            }
            .logo {
              width: 80px;
              height: 80px;
              border-radius: 50%;
              margin-bottom: 15px;
            }
            h1 {
              color: #059669;
              margin: 0 0 5px 0;
              font-size: 28px;
            }
            h2 {
              color: #64748b;
              margin: 0 0 30px 0;
              font-size: 18px;
              font-weight: normal;
            }
            .qr-code {
              margin: 20px 0;
            }
            .instructions {
              color: #475569;
              font-size: 14px;
              margin-top: 20px;
            }
            .url {
              color: #059669;
              font-size: 12px;
              word-break: break-all;
              margin-top: 15px;
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_egg-inventory-pro/artifacts/vtgmvoj5_Logo%201.jpg" 
              alt="Logo" 
              class="logo"
            />
            <h1>Wilyrash Farm</h1>
            <h2>We're Hiring!</h2>
            <div class="qr-code">
              ${svgData}
            </div>
            <p class="instructions">
              Scan this QR code with your phone camera to apply for a position at Wilyrash Farm
            </p>
            <p class="url">${applicationUrl}</p>
          </div>
          <script>
            setTimeout(() => {
              window.print();
              window.close();
            }, 500);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
          <QrCode className="w-6 h-6 text-emerald-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-800">Job Application QR Codes</h2>
          <p className="text-slate-500 text-sm">Share these codes on printed materials or social media</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Application QR Code */}
        <div className="card p-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Link2 className="w-4 h-4 text-emerald-600" />
            Job Application Form
          </h3>
          
          <div className="bg-white border-2 border-slate-200 rounded-xl p-6 flex flex-col items-center">
            <QRCodeSVG 
              id="qr-apply"
              value={applicationUrl}
              size={200}
              level="H"
              includeMargin={true}
              bgColor="#ffffff"
              fgColor="#1e293b"
            />
            <p className="text-sm text-slate-600 mt-4 font-medium">Scan to Apply</p>
          </div>

          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-500 mb-1">Direct Link:</p>
            <div className="flex items-center gap-2">
              <code className="text-xs bg-white px-2 py-1 rounded border flex-1 overflow-hidden text-ellipsis">
                {applicationUrl}
              </code>
              <Button size="sm" variant="ghost" onClick={() => copyToClipboard(applicationUrl)}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => downloadQR('apply')}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={printQR}
            >
              <Printer className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Status Check QR Code */}
        <div className="card p-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Link2 className="w-4 h-4 text-blue-600" />
            Application Status Check
          </h3>
          
          <div className="bg-white border-2 border-slate-200 rounded-xl p-6 flex flex-col items-center">
            <QRCodeSVG 
              id="qr-status"
              value={statusUrl}
              size={200}
              level="H"
              includeMargin={true}
              bgColor="#ffffff"
              fgColor="#1e293b"
            />
            <p className="text-sm text-slate-600 mt-4 font-medium">Scan to Check Status</p>
          </div>

          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-500 mb-1">Direct Link:</p>
            <div className="flex items-center gap-2">
              <code className="text-xs bg-white px-2 py-1 rounded border flex-1 overflow-hidden text-ellipsis">
                {statusUrl}
              </code>
              <Button size="sm" variant="ghost" onClick={() => copyToClipboard(statusUrl)}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => downloadQR('status')}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </div>

      {/* Usage Tips */}
      <div className="card p-5 bg-blue-50 border-blue-200">
        <h4 className="font-medium text-blue-800 mb-2">Usage Tips</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Print the QR code and display it at your farm entrance or office</li>
          <li>• Add it to job posting flyers or notice boards</li>
          <li>• Share the direct link on social media platforms</li>
          <li>• Include it in your recruitment materials or business cards</li>
        </ul>
      </div>
    </div>
  );
};

export default JobApplicationQR;
